<script setup lang="ts">
import { useVideoCallsStore } from "~/stores/videoCalls";

const props = withDefaults(
  defineProps<{ compact?: boolean; hideRoom?: boolean }>(),
  {
    compact: false,
    hideRoom: false,
  },
);
const store = useVideoCallsStore();
const now = ref(Date.now());
const serverOffsetMs = ref(0);
let countdownTimer: ReturnType<typeof setInterval> | null = null;
const snapshot = computed(() => store.roomsSnapshot);
const currentRoom = computed(() =>
  snapshot.value?.rooms.find(
    (room) => room.id === snapshot.value?.current_room_id,
  ),
);
const secondsRemaining = computed(() => {
  const deadline = snapshot.value?.closing_deadline;
  if (!deadline) return 0;
  return Math.max(
    0,
    Math.ceil(
      (Date.parse(deadline) - now.value - serverOffsetMs.value) / 1_000,
    ),
  );
});
const announcement = computed(() => {
  const notice = snapshot.value?.announcement;
  return notice && notice.id !== store.roomsAnnouncementDismissedId
    ? notice
    : null;
});
const hasError = computed(() => store.roomsError && !store.roomsOpen);
const helpRequestCount = computed(
  () => snapshot.value?.help_requests.length ?? 0,
);
const helpRequestLabel = computed(() =>
  helpRequestCount.value === 1
    ? "1 pedido de ajuda"
    : `${helpRequestCount.value} pedidos de ajuda`,
);

const openRoomsForHelp = async (): Promise<void> => {
  store.setPanelSize("medium");
  store.setRoomsOpen(true);
  await store.refreshRooms();
};

watch(
  () => snapshot.value?.server_time,
  (serverTime) => {
    if (!serverTime) return;
    const parsed = Date.parse(serverTime);
    if (Number.isFinite(parsed)) serverOffsetMs.value = parsed - Date.now();
  },
  { immediate: true },
);
watch(
  () => snapshot.value?.closing_deadline,
  (deadline) => {
    if (countdownTimer) clearInterval(countdownTimer);
    countdownTimer = null;
    now.value = Date.now();
    if (deadline) {
      countdownTimer = setInterval(() => {
        now.value = Date.now();
      }, 250);
    }
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  if (countdownTimer) clearInterval(countdownTimer);
});
</script>

<template>
  <div
    v-if="snapshot"
    class="rooms-status"
    :class="{ 'rooms-status--compact': props.compact }"
    data-testid="video-call-room-status"
  >
    <div
      v-if="props.compact && store.role === 'host' && helpRequestCount"
      class="rooms-status__help"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="video-call-compact-room-help"
    >
      <button
        type="button"
        :aria-label="`Ver ${helpRequestLabel} nas salas`"
        @click="openRoomsForHelp"
      >
        <i class="mdi mdi-hand-wave-outline" aria-hidden="true" />
        <span>{{ helpRequestLabel }}</span>
        <i class="mdi mdi-chevron-right" aria-hidden="true" />
      </button>
    </div>
    <div v-if="!props.compact && !props.hideRoom" class="rooms-status__room">
      <i class="mdi mdi-door-open" aria-hidden="true" />
      <span>{{ currentRoom?.name ?? "Sala principal" }}</span>
      <span v-if="snapshot.host_present" class="rooms-status__teacher">
        Professor presente
      </span>
    </div>
    <p
      v-if="snapshot.state === 'closing'"
      class="rooms-status__countdown"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <i class="mdi mdi-timer-sand" aria-hidden="true" />
      {{
        secondsRemaining
          ? `Todos voltam à principal em ${secondsRemaining}s`
          : "Voltando à sala principal…"
      }}
    </p>
    <div
      v-if="announcement"
      class="rooms-status__announcement"
      role="status"
      aria-live="polite"
      data-testid="video-call-room-announcement"
    >
      <i class="mdi mdi-bullhorn-outline" aria-hidden="true" />
      <div>
        <strong>Aviso do professor</strong>
        <p>{{ announcement.body }}</p>
      </div>
      <button
        type="button"
        aria-label="Fechar aviso do professor"
        @click="store.dismissRoomAnnouncement()"
      >
        <i class="mdi mdi-close" aria-hidden="true" />
      </button>
    </div>
    <p v-if="hasError" class="rooms-status__error" role="alert">
      {{ store.roomsError }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.rooms-status {
  display: grid;
  gap: 6px;
  padding: 0 14px 10px;
  font-size: 0.74rem;
}

.rooms-status__room {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  color: #445b82;
  font-weight: 700;
}

.rooms-status__help button {
  display: flex;
  width: 100%;
  min-height: 36px;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border: 1px solid #e6bc76;
  border-radius: 8px;
  background: #fff3dc;
  color: #805000;
  font: inherit;
  font-weight: 700;
  cursor: pointer;

  span {
    flex: 1;
    text-align: left;
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
}

.rooms-status__teacher {
  padding: 2px 6px;
  border-radius: 5px;
  background: #edf1ff;
  color: #3153ba;
  font-size: 0.65rem;
}

.rooms-status__countdown {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #efd5a6;
  border-radius: 8px;
  background: #fff7e8;
  color: #805000;
  font-weight: 700;
}

.rooms-status__announcement {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 9px 10px;
  border: 1px solid #cad7f4;
  border-radius: 8px;
  background: #eef3ff;
  color: #2e4f91;

  > div {
    min-width: 0;
    flex: 1;
  }

  strong {
    font-size: 0.68rem;
  }

  p {
    max-height: 90px;
    margin: 3px 0 0;
    overflow: auto;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }

  button {
    display: grid;
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    place-items: center;
    color: inherit;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid #3153ba;
      outline-offset: 2px;
    }
  }
}

.rooms-status__error {
  margin: 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff0f1;
  color: #a51f34;
}

.rooms-status--compact {
  position: absolute;
  z-index: 5;
  top: 40px;
  right: 8px;
  left: 8px;
  padding: 0;
  font-size: 0.65rem;

  .rooms-status__announcement {
    max-height: 86px;
    padding: 6px;

    p {
      max-height: 45px;
    }
  }

  .rooms-status__countdown {
    padding: 5px 6px;
  }
}
</style>
